type DrawerLocalized = { en: string; he: string };

// English is the deliberate fallback until the long-form drawer copy is professionally translated.
const en = (value: string): DrawerLocalized => ({ en: value, he: value });

export const productDrawerStrings: Record<string, DrawerLocalized> = {
  "drawer.open": en("Open product information"),
  "drawer.close": en("Close product information"),
  "drawer.nav.label": en("Product information"),
  "drawer.nav.about": en("About Qotta"),
  "drawer.nav.how": en("How it works"),
  "drawer.nav.who": en("Who it's for"),
  "drawer.nav.gettingStarted": en("Getting started"),
  "drawer.nav.pricing": en("Pricing"),
  "drawer.nav.terms": en("Terms of Service"),
  "drawer.nav.privacy": en("Privacy Policy"),
  "drawer.nav.disclaimer": en("Disclaimer"),
  "drawer.join": en("Join Qotta"),
  "drawer.contact.questions": en("Questions?"),
  "drawer.step": en("Step"),

  "drawer.about.title": en("About Qotta"),
  "drawer.about.hero": en("Your website shouldn't be where buying intent goes to die."),
  "drawer.about.intro": en(
    'Qotta is a new AI-powered product built around a simple idea: when someone reaches your website with genuine buying intent, asking them to "leave their details" shouldn\'t be the best experience we can offer.',
  ),
  "drawer.about.buyers": en("Today's buyers arrive with two main questions."),
  "drawer.about.question1": en("Can this company provide what I'm looking for?"),
  "drawer.about.question2": en("How much is it going to cost me?"),
  "drawer.about.layer": en(
    "Qotta adds an intelligent discovery layer to your website, understanding what visitors need, guiding them through the decisions that matter, and providing personalized guidance, including an indicative price estimate.",
  ),
  "drawer.about.exchange": en(
    "The visitor gets value before being asked for contact details. The business gets an opportunity with context.",
  ),
  "drawer.about.ai.title": en("Built for the AI era"),
  "drawer.about.ai.p1": en(
    "Modern AI can understand context and intent, creating buying journeys that adapt to each visitor while operating within the business's knowledge and rules.",
  ),
  "drawer.about.ai.p2": en(
    "Not just a website that publishes information, but an active part of the buying experience.",
  ),
  "drawer.about.ai.manage.title": en(
    "And AI doesn't just power the customer experience. It makes Qotta effortless to manage.",
  ),
  "drawer.about.ai.manage.p1": en(
    "When your business changes, simply tell Qotta what's new. Its AI updates the relevant knowledge and discovery experience.",
  ),
  "drawer.about.ai.manage.p2": en(
    "No rebuilding flows. No complex decision trees. No software project.",
  ),
  "drawer.about.ai.manage.emphasis1": en("Keeping Qotta updated is simply telling it what's new."),
  "drawer.about.start.title": en("We're starting where Qotta matters most"),
  "drawer.about.start.p1": en(
    "Qotta is built for businesses where understanding the customer's needs, project and expected budget is essential before a meaningful sales conversation can begin.",
  ),
  "drawer.about.start.p2": en(
    "We're starting there and building toward a future where every high-intent website visit can become a better buying experience.",
  ),
  "drawer.about.close1": en("Turn intent into understanding. Turn understanding into opportunity."),
  "drawer.about.contact": en("Contact Qotta"),
  "drawer.about.email": en("info@qotta.co"),

  "drawer.how.title": en("How Qotta works"),
  "drawer.how.intro": en(
    "Qotta adds a guided discovery experience between website traffic and the traditional sales conversation.",
  ),
  "drawer.how.step1.title": en("Understand the business"),
  "drawer.how.step1.body": en(
    "Qotta learns the products, services, pricing logic and important business context needed to guide potential customers. Source prices and pricing calculations exclude tax.",
  ),
  "drawer.how.step2.title": en("Guide the visitor"),
  "drawer.how.step2.body": en(
    "Instead of immediately asking for contact details, Qotta asks relevant questions one decision at a time. The journey adapts to what the visitor tells us.",
  ),
  "drawer.how.step3.title": en("Build the picture"),
  "drawer.how.step3.body": en(
    "As the visitor progresses, Qotta builds a structured understanding of what they are actually looking for.",
  ),
  "drawer.how.step4.title": en("Provide useful value"),
  "drawer.how.step4.body": en(
    "When sufficient information is available, Qotta can present a recommended solution and an indicative personalized price estimate based on the business's pricing knowledge and assumptions. When a tax rate is available, the estimate shows prices before and after tax. Otherwise, additional taxes may apply depending on the project and location.",
  ),
  "drawer.how.step5.title": en("Continue with context"),
  "drawer.how.step5.body": en(
    "When the visitor chooses to continue, the business receives the contact together with the Discovery context. The salesperson can begin the conversation knowing substantially more than they would receive from a traditional contact form.",
  ),
  "drawer.how.close1": en("The goal isn't to replace the salesperson."),
  "drawer.how.close2": en("It's to make the conversation that reaches them better."),

  "drawer.who.title": en("Who Qotta is for"),
  "drawer.who.intro": en(
    "Qotta is designed for businesses where a potential customer usually needs to make several decisions before anyone can give them a meaningful answer.",
  ),
  "drawer.who.bestFit": en("Best-fit businesses typically have:"),
  "drawer.who.trait1": en("Products or services that vary by customer requirements"),
  "drawer.who.trait2": en("Meaningful differences in project scope or configuration"),
  "drawer.who.trait3": en("Pricing that can be estimated before a final professional review"),
  "drawer.who.trait4": en("Website traffic with real commercial intent"),
  "drawer.who.trait5": en("A sales process where context matters"),
  "drawer.who.trait6": en('Existing "contact us" or "request a quote" conversion points'),
  "drawer.who.examples": en("Examples may include:"),
  "drawer.who.example1": en("Custom kitchens"),
  "drawer.who.example2": en("Home renovation"),
  "drawer.who.example3": en("HVAC and climate systems"),
  "drawer.who.example4": en("Pergolas and shading"),
  "drawer.who.example5": en("Windows and doors"),
  "drawer.who.example6": en("Solar installations"),
  "drawer.who.example7": en("Roofing"),
  "drawer.who.example8": en("Pools and spas"),
  "drawer.who.example9": en("Custom cabinetry"),
  "drawer.who.example10": en("Landscaping"),
  "drawer.who.example11": en("Other configurable products and project-based services"),
  "drawer.who.notFit.title": en("When Qotta may not be the right fit"),
  "drawer.who.notFit.p1": en(
    "Qotta is not intended to replace a simple checkout experience for fixed-price products.",
  ),
  "drawer.who.notFit.p2": en(
    "It is most valuable when the visitor needs guidance before they are ready for a real sales conversation.",
  ),

  "drawer.getting.title": en("Getting started with Qotta"),
  "drawer.getting.intro": en(
    "The easiest way to understand Qotta is to see what it could look like on your own website.",
  ),
  "drawer.getting.step1.title": en("Enter your website"),
  "drawer.getting.step1.body": en("Start from the Qotta homepage and enter your website address."),
  "drawer.getting.step2.title": en("See your Qotta preview"),
  "drawer.getting.step2.body": en(
    "Qotta uses the information available on your website to create an initial understanding of your business and demonstrate how a guided Discovery could work for your visitors.",
  ),
  "drawer.getting.step3.title": en("Review and refine"),
  "drawer.getting.step3.body": en(
    "Review what Qotta understood about your business, the proposed customer journey, recommendations and pricing logic. Source prices exclude tax. Review the inferred country and tax information in Settings and request any corrections through Journey before publishing. A tax-inclusive estimate is also shown when a rate is available. This initial setup is a starting point and can be refined before publishing.",
  ),
  "drawer.getting.step4.title": en("Join Qotta"),
  "drawer.getting.step4.body": en(
    'When you\'re ready to continue, use "Join Qotta" to move from the preview experience into the joining process. If you have already completed the website preview, Qotta carries that context forward rather than restarting onboarding.',
  ),
  "drawer.getting.step4.free": en("Start with your first 25 successful discoveries free."),
  "drawer.getting.step5.title": en("Prepare to go live"),
  "drawer.getting.step5.body": en(
    "Complete the remaining setup, review the experience and prepare Qotta for connection to the website.",
  ),

  "drawer.pricing.title": en("Pricing"),
  "drawer.pricing.heading": en("Simple, outcome-based pricing"),
  "drawer.pricing.free": en("Your first 25 successful discoveries are free."),
  "drawer.pricing.after.prefix": en("After that, Qotta costs"),
  "drawer.pricing.after.price": en("$10 per successful discovery"),
  "drawer.pricing.after.suffix": en("."),
  "drawer.pricing.definition": en(
    "A successful discovery is counted only when a visitor leaves their contact details after completing their Qotta journey.",
  ),
  "drawer.pricing.noContact": en("No contact details, no charge."),
  "drawer.pricing.commitment": en(
    "No subscription commitment. You pay for outcomes, not website traffic.",
  ),
  "drawer.pricing.cta": en("Start with 25 free"),

  "drawer.terms.title": en("Terms of Service"),
  "drawer.terms.lastUpdated": en("Last updated: October 7, 2026"),
  "drawer.terms.acceptance.title": en("Acceptance of terms"),
  "drawer.terms.acceptance.body": en(
    "Qotta is operated by Qotta L.L.C., a Wyoming limited liability company (Qotta, we or us). These Terms govern business customers using Qotta (you or the customer). By registering for, purchasing or using Qotta for a business, you agree on that business's behalf and confirm your authority to bind it. These Terms are not a website visitor agreement merely because a visitor participates in a Discovery.",
  ),
  "drawer.terms.service.title": en("Description of the service"),
  "drawer.terms.service.body": en(
    "Qotta provides software and AI-assisted tools for guided website Discoveries, recommendations, indicative estimates, lead qualification and handoff. Qotta does not supply the underlying products or professional or service work offered by your business and is not a party to transactions between you and your visitors or customers.",
  ),
  "drawer.terms.information.title": en("Information supplied to Qotta"),
  "drawer.terms.information.body": en(
    "You are responsible for the accuracy, legality and suitability of products, services, pricing information, business rules and other material you provide. Keep this information current and ensure you have the rights and authority necessary to supply it and permit its use under these Terms.",
  ),
  "drawer.terms.use.title": en("Authorized use"),
  "drawer.terms.use.body": en(
    "Subject to these Terms and your selected plan, Qotta grants you a limited, non-exclusive right to access and use the service for your business, including embedding Discoveries on websites you are authorized to manage. Use the service only for legitimate business purposes, in accordance with applicable law and with appropriate authority over submitted information.",
  ),
  "drawer.terms.misuse.title": en("Prohibited misuse"),
  "drawer.terms.misuse.body": en(
    "You must not misuse the service, interfere with its operation, attempt unauthorized access, submit unlawful or harmful material, mislead visitors, or use outputs to facilitate fraud or abuse.",
  ),
  "drawer.terms.account.title": en("Accounts and security"),
  "drawer.terms.account.body": en(
    "You are responsible for authorized users, their use of Qotta and the security of your account credentials. Protect credentials, limit access to authorized users and promptly report suspected unauthorized access to info@qotta.co. Do not share access to circumvent your plan's limits.",
  ),
  "drawer.terms.ai.title": en("AI-generated and automated outputs"),
  "drawer.terms.ai.body": en(
    "Qotta uses AI and automated processing. Outputs may be incomplete, inaccurate or unsuitable for a particular situation. Review outputs appropriately before relying on them or presenting them as your business's recommendations. Do not use them as the sole basis for important financial, contractual, technical or safety decisions.",
  ),
  "drawer.terms.estimates.title": en("Indicative estimates"),
  "drawer.terms.estimates.body": en(
    "Estimates, price ranges and recommendations are informational and indicative only, based on available inputs. They are not binding quotations, offers, guarantees or professional advice. The relevant business determines final scope and pricing following any necessary review, inspection or validation and is responsible for communicating this distinction to visitors.",
  ),
  "drawer.terms.decisions.title": en("Final commercial decisions"),
  "drawer.terms.decisions.body": en(
    "Your business remains responsible for its products and services, representations to visitors, final recommendations, professional review, customer communications, contracts, commercial terms and final pricing. Qotta's tools do not replace your business's judgment or obligations to its visitors and customers.",
  ),
  "drawer.terms.visitorInformation.title": en("Discovery and lead information"),
  "drawer.terms.visitorInformation.body": en(
    "Qotta may process information submitted during a Discovery to provide the service. When a visitor chooses to continue, relevant visitor-submitted contact information and Discovery context may be made available to the applicable business to handle the inquiry or lead. Processing is subject to Qotta's Privacy Policy, available at /privacy. You are responsible for appropriate notices, permissions and lawful handling of information received by your business, including under your own privacy practices.",
  ),
  "drawer.terms.content.title": en("Customer content"),
  "drawer.terms.content.body": en(
    "You retain ownership of your content. You grant Qotta a limited, non-exclusive license to host, process, analyze, transform and display it only as necessary to provide, maintain and improve the service for your business, subject to the Privacy Policy and its confidentiality restrictions. This includes processing by service providers as necessary to operate Qotta; it does not authorize sharing your confidential business information with other customers or using it for their benefit. You represent that you have all rights and authority needed to grant this license.",
  ),
  "drawer.terms.ip.title": en("Intellectual property"),
  "drawer.terms.ip.body": en(
    "Qotta and its licensors retain all rights in the software, product, workflows, user interface, documentation, branding and other Qotta technology and materials. Using the service does not transfer ownership of these to you. These rights do not include ownership of your content.",
  ),
  "drawer.terms.payment.title": en("Fees, payments and subscriptions"),
  "drawer.terms.payment.body": en(
    "Fees, usage charges and any billing cycles are shown in the applicable plan or checkout when you purchase. You are responsible for applicable charges and taxes disclosed there. If you select a subscription, it may renew according to the selected plan and purchase terms until canceled. Qotta may use third-party payment processors and/or a Merchant of Record; those providers may handle payment processing, invoicing, taxes and refunds as applicable, subject to their purchase terms. Cancellation procedures, when cancellation takes effect and any refund eligibility are governed by the applicable plan or checkout terms and applicable law. Contact info@qotta.co for billing or cancellation assistance.",
  ),
  "drawer.terms.availability.title": en("Service availability"),
  "drawer.terms.availability.body": en(
    "Qotta uses commercially reasonable efforts to operate the service. Availability may be affected by maintenance, technical issues, third-party dependencies or events outside Qotta's reasonable control. Unless expressly agreed in writing, no uninterrupted or error-free availability or specific service level is promised.",
  ),
  "drawer.terms.changesService.title": en("Changes to the service"),
  "drawer.terms.changesService.body": en(
    "Features, integrations and access arrangements may change as Qotta develops. Material changes will be communicated where appropriate.",
  ),
  "drawer.terms.warranties.title": en("Disclaimer of warranties"),
  "drawer.terms.warranties.body": en(
    "To the extent permitted by applicable law, and except for express commitments in a written agreement with Qotta, the service and its outputs are provided as is and as available. Qotta disclaims implied warranties of merchantability, fitness for a particular purpose and non-infringement. Qotta does not warrant uninterrupted or error-free operation, accurate or complete outputs, or a particular business result. Nothing in these Terms excludes warranties or rights that cannot lawfully be excluded.",
  ),
  "drawer.terms.liability.title": en("Limitation of liability"),
  "drawer.terms.liability.body": en(
    "To the extent permitted by applicable law, Qotta is not liable for indirect, incidental, special, consequential, exemplary or punitive damages, or loss of profits, revenue, business opportunities, goodwill or data arising out of or relating to these Terms or the service, even if advised of their possibility. Qotta's aggregate liability arising out of or relating to these Terms or the service, under any legal theory, will not exceed the total fees paid or payable by the affected customer for the affected service during the 12 months immediately preceding the event giving rise to the claim. These exclusions and this cap do not apply to fraud, willful misconduct, gross negligence or any liability that cannot be excluded or limited under applicable law.",
  ),
  "drawer.terms.indemnity.title": en("Customer indemnification"),
  "drawer.terms.indemnity.body": en(
    "You will defend Qotta against third-party claims arising from your customer-provided content (including infringement caused by your materials), unlawful or unauthorized use, or your underlying products, services or representations to visitors. You will indemnify Qotta for damages and reasonable costs finally awarded or agreed in a settlement you approve. This obligation applies only to the extent the claim arises from those customer activities and does not cover harm caused by Qotta's own breach of these Terms, negligence or misconduct. Qotta must promptly notify you, allow you to control the defense and provide reasonable cooperation at your expense. You may not settle in a way that admits fault by Qotta or imposes obligations on Qotta without its written consent, which will not be unreasonably withheld.",
  ),
  "drawer.terms.termination.title": en("Termination"),
  "drawer.terms.termination.body": en(
    "You may cancel your service or subscription under the applicable plan or checkout terms, with assistance available at info@qotta.co. Qotta may suspend or terminate access for misuse, material breach, security risk, legal requirements or non-payment. Where reasonably practicable, Qotta will give notice and an opportunity to resolve the issue; immediate action may be necessary to protect the service or comply with law. When termination takes effect, your right to use the service ends and you must stop using and embedding it. Accrued charges remain due; cancellation and refunds remain subject to applicable purchase terms and law. Retention and deletion of information remain subject to the Privacy Policy and applicable legal obligations; termination does not promise immediate deletion. Ownership, accrued payment obligations, disclaimers, liability and indemnification survive to the extent needed to give them effect.",
  ),
  "drawer.terms.disputes.title": en("Governing law and disputes"),
  "drawer.terms.disputes.body": en(
    "Questions or disputes may be raised at info@qotta.co. These Terms and disputes arising out of or relating to these Terms or the service are governed by the laws of the State of Wyoming, without regard to conflict-of-laws principles. Any dispute requiring judicial resolution must be brought in the state or federal courts located in Wyoming with jurisdiction over the dispute, and you and Qotta consent to the jurisdiction and venue of those courts. Nothing in these Terms limits mandatory consumer or statutory rights that cannot legally be waived, including any applicable right to bring a claim in another court.",
  ),
  "drawer.terms.changesTerms.title": en("Changes to these terms"),
  "drawer.terms.changesTerms.body": en(
    "Qotta may update these Terms as the service or commercial offering changes. The current version will be made available through Qotta. Qotta will notify customers of material changes and their effective date in advance where reasonably practicable, except where an earlier change is required by law or to address urgent security concerns. Continued use after the effective date constitutes acceptance; if you do not agree, you may cancel under the applicable plan or checkout terms. Changes do not retroactively alter accrued rights or obligations.",
  ),
  "drawer.terms.contact.title": en("Contact"),
  "drawer.terms.contact.body": en(
    "Questions about these Terms can be sent to info@qotta.co.",
  ),

  "drawer.privacy.title": en(
    "Privacy Policy",
  ),
  "drawer.privacy.lastUpdated": en(
    "Last updated: October 8, 2026",
  ),
  "drawer.privacy.intro": en(
    "Qotta is operated by Qotta L.L.C., a Wyoming limited liability company (\"Qotta\", \"we\", \"us\"). This Privacy Policy explains how we process information in connection with our business software service and Qotta Discovery experiences.",
  ),
  "drawer.privacy.roles.title": en(
    "Who this policy covers",
  ),
  "drawer.privacy.roles.body": en(
    "Business Customers are businesses that configure and operate Qotta. Visitors are people who interact with a Qotta Discovery on a Business Customer's website. The information we process depends on your role, the business's configuration and use of Qotta, and the information you voluntarily provide.",
  ),
  "drawer.privacy.visitors.title": en(
    "Visitor information",
  ),
  "drawer.privacy.visitors.info1": en(
    "Discovery answers, project requirements, and notes or other free-text content submitted during Discovery.",
  ),
  "drawer.privacy.visitors.info2": en(
    "Contact details, such as name, email address and phone number, voluntarily submitted when continuing as a lead.",
  ),
  "drawer.privacy.visitors.info3": en(
    "Discovery and session identifiers, interaction and navigation history, timestamps, and the source website URL, page path/query and page title where applicable.",
  ),
  "drawer.privacy.visitors.info4": en(
    "Generated recommendations, indicative estimates and other Discovery results.",
  ),
  "drawer.privacy.customers.title": en(
    "Business Customer information",
  ),
  "drawer.privacy.customers.info1": en(
    "Account and authentication information, including name, email address and profile information supplied through authentication, and user, customer and business identifiers.",
  ),
  "drawer.privacy.customers.info2": en(
    "Business profiles, website information, products, services, pricing and business rules.",
  ),
  "drawer.privacy.customers.info3": en(
    "Configuration and context, instructions, clarification answers and proprietary business knowledge supplied to Qotta.",
  ),
  "drawer.privacy.customers.info4": en(
    "Operational activity related to configuring and using Qotta, including changes, lead-management notes and related activity.",
  ),
  "drawer.privacy.operational.title": en(
    "Operational information",
  ),
  "drawer.privacy.operational.info1": en(
    "Logs, errors, security and diagnostic events, AI usage metadata, timestamps, and request, customer and session identifiers.",
  ),
  "drawer.privacy.operational.info2": en(
    "Usage and technical information used to operate the service, such as processing status, timings, input sizes and AI token usage.",
  ),
  "drawer.privacy.network": en(
    "Infrastructure and service providers may receive normal network request metadata as part of operating hosted services.",
  ),
  "drawer.privacy.use.title": en(
    "How we use information",
  ),
  "drawer.privacy.use1": en(
    "Providing and operating Discovery, including preserving progress and generating recommendations and indicative estimates.",
  ),
  "drawer.privacy.use2": en(
    "Qualifying leads and providing the relevant business with Discovery information and contact details.",
  ),
  "drawer.privacy.use3": en(
    "Providing business configuration and management tools, website and business analysis, and AI-assisted generation and configuration.",
  ),
  "drawer.privacy.use4": en(
    "Authentication and account administration.",
  ),
  "drawer.privacy.use5": en(
    "Maintaining, securing, troubleshooting and monitoring the service.",
  ),
  "drawer.privacy.use6": en(
    "Providing customer support.",
  ),
  "drawer.privacy.use7": en(
    "Enforcing agreements, preventing misuse and complying with legal obligations.",
  ),
  "drawer.privacy.use8": en(
    "Improving service reliability and functionality in connection with operating Qotta, without repurposing confidential customer information for unrelated commercial uses.",
  ),
  "drawer.privacy.business.title": en(
    "Your relationship with the relevant business",
  ),
  "drawer.privacy.business.body": en(
    "Discovery information is processed in connection with the relevant Business Customer's use of Qotta. Discovery answers may be stored and made available to that business during the Discovery lifecycle, before contact information is submitted. When you submit contact information or choose to continue, Qotta may provide the business with those details and your Discovery context. The business may process that information under its own privacy practices. Qotta does not sell the underlying product or service offered by the business. Privacy responsibilities depend on the particular processing activity, relationship and applicable law.",
  ),
  "drawer.privacy.confidentiality.title": en(
    "Business data and confidentiality",
  ),
  "drawer.privacy.confidentiality.body": en(
    "Business Customers retain rights in the business information they provide. Qotta processes that information to provide, maintain, secure and support the service. Qotta does not intentionally disclose one customer's confidential business information to another customer. We use technical and organizational measures designed to separate customer environments and reduce unauthorized access.",
  ),
  "drawer.privacy.ai.title": en(
    "AI-assisted processing and OpenAI",
  ),
  "drawer.privacy.ai.p1": en(
    "Qotta uses OpenAI as an AI service provider for website analysis, business-context generation and configuration assistance. Business information, website content, instructions, pricing, rules and configuration may be sent to OpenAI when needed for these functions.",
  ),
  "drawer.privacy.ai.p2": en(
    "Ordinary Visitor Discovery answers and contact submissions are not sent directly to OpenAI in the current Discovery runtime flow. Personal information may nevertheless be included in business content, public website content or free-text business inputs processed through these AI functions.",
  ),
  "drawer.privacy.ai.p3": en(
    "Qotta's OpenAI API requests use store: false. Under OpenAI's current API/business policies, API data is not used to train OpenAI models by default unless the API customer opts in. OpenAI may retain certain API data for abuse monitoring or other permitted operational or legal purposes under its applicable policies. The store: false setting does not mean that OpenAI retains no data.",
  ),
  "drawer.privacy.ai.link": en(
    "OpenAI API data policies",
  ),
  "drawer.privacy.providers.title": en(
    "Service providers and other disclosures",
  ),
  "drawer.privacy.providers.body": en(
    "We use Google / Firebase / Google Cloud for infrastructure and authentication, and OpenAI for AI processing. We also use third-party service providers for cloud hosting, databases, authentication, AI, communications, security and monitoring, support, and payment processing. Qotta uses third-party payment processors and/or Merchant of Record providers to process payments, billing, invoicing, taxes, refunds and related transaction information. Providers process information to perform services for Qotta subject to applicable contractual terms. We may also disclose information where required by law or reasonably necessary to enforce agreements, prevent misuse or protect rights and safety.",
  ),
  "drawer.privacy.storage.title": en(
    "Browser storage and local persistence",
  ),
  "drawer.privacy.storage.body": en(
    "Qotta uses localStorage, sessionStorage, Firebase authentication persistence, Firestore browser persistence/cache and similar browser storage needed for product functionality. These mechanisms support session continuity, Discovery progress, drafts, authentication, cached application and customer data, and pending writes. Browser-stored information may remain after a session ends or you sign out. Browser settings may allow you to clear or limit storage, which can affect functionality. These mechanisms are not all cookies; authentication and other service providers may use their own cookies.",
  ),
  "drawer.privacy.advertising.title": en(
    "Sale and advertising",
  ),
  "drawer.privacy.advertising.body": en(
    "Qotta does not sell Visitor or Business Customer personal information for monetary consideration. We do not currently use personal information for third-party advertising or operate advertising pixels, session-replay tools or third-party advertising tracking technologies.",
  ),
  "drawer.privacy.retention.title": en(
    "Data retention",
  ),
  "drawer.privacy.retention.body": en(
    "Qotta retains information only for as long as reasonably necessary for the purposes described in this policy, including providing the service, maintaining business records, resolving disputes, security, fraud prevention and legal obligations. Retention periods may vary by information category and relationship. Information may be deleted, anonymized or retained where required or permitted by law. Some information may remain temporarily in backups, logs or technical systems after deletion from active systems. Deletion is not necessarily immediate.",
  ),
  "drawer.privacy.choices.title": en(
    "Privacy rights and requests",
  ),
  "drawer.privacy.choices.body": en(
    "Depending on applicable law and circumstances, individuals may have rights to access, correct or delete personal information, restrict or object to processing, receive portable information, or withdraw consent where consent is the applicable basis. Requests can be sent to info@qotta.co. We may need to verify your identity and the scope of your request. Where Qotta processes Visitor information on behalf of or in connection with a Business Customer, we may direct or coordinate the request with that business as appropriate. Available rights, limitations and exceptions depend on applicable law.",
  ),
  "drawer.privacy.international.title": en(
    "International processing",
  ),
  "drawer.privacy.international.body": en(
    "Qotta and its service providers may process information in the United States and other countries where they operate. Where required, Qotta will use appropriate safeguards for international transfers.",
  ),
  "drawer.privacy.security.title": en(
    "Security",
  ),
  "drawer.privacy.security.body": en(
    "Qotta uses technical and organizational measures designed to protect information against unauthorized access, loss, misuse or disclosure. No online service can guarantee absolute security.",
  ),
  "drawer.privacy.changes.title": en(
    "Changes to this policy",
  ),
  "drawer.privacy.changes.body": en(
    "We may update this policy. The current version and its Last updated date will be available through Qotta. Material changes may be communicated where appropriate.",
  ),
  "drawer.privacy.contact.title": en(
    "Contact",
  ),
  "drawer.privacy.contact.body": en(
    "For privacy questions or requests, contact Qotta L.L.C. at info@qotta.co.",
  ),

  "drawer.disclaimer.title": en("Disclaimer"),
  "drawer.disclaimer.lastUpdated": en("Last updated: October 8, 2026"),
  "drawer.disclaimer.lead": en(
    "Qotta provides guidance and indicative estimates, not final quotes.",
  ),
  "drawer.disclaimer.p1": en(
    "Recommendations, price ranges and other outputs generated during a Qotta Discovery are based on the information available at that point, including visitor answers and information supplied by the relevant business.",
  ),
  "drawer.disclaimer.p2": en(
    "They are intended to help visitors understand likely options and approximate pricing.",
  ),
  "drawer.disclaimer.p3": en(
    "They are not binding quotations, offers, professional assessments or guarantees of final price.",
  ),
  "drawer.disclaimer.p4": en(
    "Actual pricing may change following professional review, site inspection, technical validation, measurements, product availability, configuration changes, taxes, fees or other factors determined by the relevant business.",
  ),
  "drawer.disclaimer.tax": en(
    "Where tax-inclusive estimates are displayed, they are calculated using the tax rate configured for the relevant business. Actual taxes may differ depending on the project, location and applicable tax rules.",
  ),
  "drawer.disclaimer.p5": en(
    "AI-generated or automated outputs may occasionally be incomplete or inaccurate and should not be treated as the sole basis for important financial, contractual, technical or safety decisions.",
  ),
  "drawer.disclaimer.p6": en(
    "The relevant business remains responsible for its products, services, final recommendations, commercial terms and final pricing.",
  ),
  "drawer.disclaimer.p7": en(
    "Qotta does not itself provide the underlying products, professional services or other services presented or offered by businesses through their Qotta Discoveries.",
  ),
};
